import assert from "assert"

import { getGlobalDbName, getTenantDbName } from "./dbNames"
import { ensureMongooseConnection } from "./ensureMongooseConnection"
import { getRegisteredModels, registerModels, type ModelsScope } from "./registerModels"
import { hasRegisteredPolicy, type AppAbility } from "./acl"


export type LoadModelCtx = {
  tenantId?: string;
  req?: {
    session?: {
      user?: {
        currentTenantId?: string;
      }
    } | null;
  };
  ability?: AppAbility;
};

const loadModelFromDb = async (
  modelName: string,
  dbName: string,
  scope: ModelsScope,
) => {
  const schemas = getRegisteredModels(scope)
  const schema = schemas[modelName]
  assert(schema, `Model ${modelName} not registered. Available models: ${Object.keys(schemas).join(", ")}`)

  const modelConnection = await ensureMongooseConnection(dbName)

  if (!modelConnection.models[modelName]) {
    modelConnection.model(modelName, schema)
  }

  return modelConnection.models[modelName]
}

const normalizeTenantId = (value: unknown): string | null => {
  if (typeof value !== "string") return null
  const tenantId = value.trim()
  return tenantId || null
}

export const getTenantIdFromLoadModelCtx = (ctx: LoadModelCtx): string => {
  const tenantId =
    normalizeTenantId(ctx.tenantId)
    ?? normalizeTenantId(ctx.req?.session?.user?.currentTenantId)
  assert(tenantId, "Tenant ID is missing from ctx (expected ctx.tenantId or ctx.req.session.user.currentTenantId)")
  return tenantId
}

export const models = {
  register: registerModels,

  getUnsafe: async (modelName: string, ctx: LoadModelCtx) => {
    const tenantId = getTenantIdFromLoadModelCtx(ctx)
    const dbName = getTenantDbName(tenantId)
    return loadModelFromDb(modelName, dbName, "tenant")
  },

  get: async (modelName: string, ctx: LoadModelCtx) => {
    const model = await models.getUnsafe(modelName, ctx)
    const resolvedAbility = ctx.ability

    const isProtected = hasRegisteredPolicy(modelName)
    if (!isProtected) {
      return model
    }

    if (!resolvedAbility) {
      throw new Error(
        `Model "${modelName}" is ACL-protected. Set ctx.ability or use models.getUnsafe(...) explicitly.`,
      )
    }

    if (typeof (model as { acl?: unknown }).acl !== "function") return model
    return (model as { acl: (appAbility: AppAbility) => typeof model }).acl(resolvedAbility)
  },

  getGlobal: async (modelName: string, ctx: LoadModelCtx) => {
    const dbName = getGlobalDbName()
    return loadModelFromDb(modelName, dbName, "global")
  },
} as const
